<template>
  <div v-if="design" class="fill-height overflow-auto">

    <error-dialog :status="loadDesignStatus" />
    <error-dialog :status="saveDesignStatus" />
    <error-dialog :status="submitDesignStatus" />
    <error-dialog :status="deleteDesignStatus" />
    <error-dialog :status="handleUploadStatus" />
    <error-dialog :status="handleUploadStaticStatus" />
    <error-dialog :status="cloneStatus" />
    <error-dialog :status="submitMediaStatus" />
    <error-dialog :status="deleteMediaStatus" />
    <error-dialog :status="updateMediaDescriptionStatus" />

    <input v-show="false" ref="upload-static-image-input" type="file" accept="image/jpeg,image/png" />

    <dialog-scroll-lock v-model="showHelp" width="800">
      <v-card>
        <v-card-title>
          <div>Getting started</div>
          <v-spacer />
          <v-btn icon @click="showHelp = false"><v-icon>{{mdiClose}}</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle>
          Click the help button <v-icon small v-html="mdiHelpCircle" /> to bring up this dialog at any time
        </v-card-subtitle>
        <v-divider />
        <template v-if="isApplying">
          <v-card-text class="pt-5">
            <b>For applicants</b><br>This is the same tool that is used to add designs to our catalog, but designs submitted during the application process will <b>not</b> be publicly released without a licensing agreement in place.
          </v-card-text>
          <v-divider />
        </template>
        <v-card-text class="pt-5">
          <ul>
            <li>Give your design a logical name. This is <b>publicly visible</b> in the catalog.</li>
            <li>Upload image(s) for your cover using the <v-icon small v-html="mdiImage" /> button. To fill the card exactly, an image should have a 5 x 7 aspect ratio. The cover you design will be used as the preview in the catalog.</li>
            <li>Use the <v-icon small v-html="mdiTextBox" /> button to add text using our built-in fonts. You can make any text user-editable by toggling "Allow user editing" in the entity context menu (right-click or click the <v-icon small v-html="iconPathBlobSettings" /> button at the bottom of the screen).</li>
            <li>The entity context menu also gives you the ability to fine-tune the position of an entity by entering exact coordinates.</li>
            <li>You can add an image template slot for the user to drop in custom photos using the <v-icon small v-html="mdiImageEdit" /> button (make sure you set a placeholder image).</li>
            <li>Use the <v-icon small v-html="mdiTexture" /> background option in the <v-icon small v-html="iconPathPageSettings" /> card settings menu to add a background / texture to every page in your card.</li>
            <li>If you are adding text that corresponds to an invitation <i>event</i> (e.g. the text shows the start time), you should add a <i>smart field</i> using the <v-icon small v-html="mdiCalendar" /> button. Note that you can customize or randomly generate sample event details.</li>
            <li>Use the <v-icon small v-html="mdiPalette" /> button to set a default theme for your design (background, confetti, envelope stamp, font, and colors).</li>
            <li>Use the <v-icon small v-html="mdiTag" /> button to add category tags to your design. <b>This is very important to make your design show up in search results</b>.</li>

            <template v-if="!isApplying">
              <li>Use the preview button <v-icon small>{{mdiFileFind}}</v-icon> to see what your card will look like in different modes and make sure any user-editable sections will work as expected.</li>
              <li>Use the <v-icon small>{{mdiCheck}}</v-icon> button to save your changes.</li>
              <li>When you're satisfied with your design (and have saved it), click the <v-icon small>{{mdiFileUpload}}</v-icon> button to submit it to us.</li>
            </template>
          </ul>
          <br>
          <p><b>Please make sure you own the rights to any content you add!</b></p>
          <!--
          <br>
          <div class="text--label-active">Shortcuts</div>
          <div><code>TAB</code> Toggle element selection</div>
          <div><code>?</code> Show the "Advanced settings" pop-up</div>
          <div><code>&larr;</code> <code>&uarr;</code> <code>&rarr;</code> <code>&darr;</code> Move element (aligned to 8px grid)</div>
          <div><code>SHIFT</code> + <code>&larr;</code> <code>&uarr;</code> <code>&rarr;</code> <code>&darr;</code> Expand / contract element (aligned to 8px grid)</div>
          <div><code>OPT</code> + <code>&larr;</code> <code>&uarr;</code> <code>&rarr;</code> <code>&darr;</code> Change text alignment</div>
          <div><code>+</code> <code>_</code> Increase / decrease font size</div>
          -->
        </v-card-text>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock v-if="showAddMedia" width="500" v-model="showAddMedia">
      <v-card>
        <v-btn class="float-right ma-3" icon @click="showAddMedia = false"><v-icon v-html="mdiClose" /></v-btn>
        <v-card-title>Add media</v-card-title>
        <v-card-text>
          <v-select
            label="Media type"
            :items="[
              { text: 'None', value: null },
              { text: 'Frame / border', value: 'frame' },
              { text: 'Texture', value: 'texture' },
              { text: 'Background', value: 'background' }
            ]"
            v-model="addMedia.type"
          />

          <template v-if="!addMedia.type">
            <div class="text--label-active mb-6">A static image to be used as an accent anywhere within the design.</div>
            <v-file-input dense outlined single-line accept="image/*" label="Image file" v-model="addMedia.img" />
          </template>

          <template v-else-if="addMedia.type == 'background'">
            <div class="text--label-active mb-6">A static image file with an aspect ratio of 5x7 and dimensions of at least 600x840. For animated backgrounds, you can also add a looping video file with the same aspect ratio. If you omit the static image for animated backgrounds, the first frame of your video will be automatically extracted. <span class="warning--text">Use of this type is discouraged, as users will not be able to adjust the background color behind it. Use the <i>Texture</i> type instead if possible.</span></div>
            <v-file-input dense outlined single-line accept="image/*" label="Image file" v-model="addMedia.img" />
            <v-file-input dense outlined single-line accept="video/*" label="Video file" v-model="addMedia.video" />
          </template>

          <template v-else-if="addMedia.type == 'frame'">
            <div class="text--label-active mb-6">A static image file designed to align with a border / edge of the card.</div>
            <v-file-input dense outlined single-line accept="image/*" label="Image file" v-model="addMedia.img" />
          </template>

          <template v-else-if="addMedia.type == 'texture'">
            <div class="text--label-active mb-6">A partially-transparent static image file with an aspect ratio of 5x7 and dimensions of at least 600x840 that can be overlaid on other content in the card or used as a background in conjunction with a user-defined color.</div>
            <v-file-input dense outlined single-line accept="image/png" label="Image file" v-model="addMedia.img" />
          </template>

          <v-textarea
            dense label="Description" outlined hide-details auto-grow rows="3"
            v-model="addMedia.description"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" :loading="submitMediaStatus.pending" @click="submitMedia">Submit</v-btn>
        </v-card-actions>

      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock v-if="editEvent" content-class="fill-height" v-model="editEvent" width="500">
      <v-card class="fill-height overflow-auto">
        <v-btn class="float-right ma-3" icon @click="editEvent = false"><v-icon v-html="mdiClose" /></v-btn>
        <v-card-title>Invitation settings</v-card-title>
        <v-card-subtitle>Where possible, invitations should include smart text fields that automatically link to the details of the user's event. This makes it so users don't have to redundantly enter their event details and reduces typos. To make a smart text field, open the settings for a selected text box <v-icon small v-html="iconPathBlobSettings" /> and click "Link text to event".</v-card-subtitle>
        <v-divider />
        <v-card-title>Placeholder event</v-card-title>
        <v-card-subtitle>For invitations, these event details will automatically link to smart fields as an example. Use the <v-icon small v-html="mdiShuffleVariant" /> buttons to automatically generate random placeholder event properties.</v-card-subtitle>

        <v-card-text class="pr-13">
          <div class="relative">
            <v-menu bottom offset-y content-class="white">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" style="top: 6px; right: -44px;" absolute icon color="primary">
                  <v-icon v-html="mdiShuffleVariant" />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="hint in NAME_HINTS" :key="'name-' + hint"
                  @click="design.event = { ...design.event, ...generateName(hint) }; applyEventLinks();"
                >
                  <v-list-item-title v-text="hint" />
                </v-list-item>
              </v-list>
            </v-menu>

            <v-menu bottom offset-y content-class="white">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" style="top: 102px; right: -44px;" absolute icon color="primary">
                  <v-icon v-html="mdiShuffleVariant" />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="hint in DATETIME_HINTS" :key="'datetime-' + hint"
                  @click="design.event = { ...design.event, ...generateDatetime(hint) }; applyEventLinks();"
                >
                  <v-list-item-title v-text="hint" />
                </v-list-item>
              </v-list>
            </v-menu>

            <v-menu bottom offset-y content-class="white">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" style="top: 198px; right: -44px;" absolute icon color="primary">
                  <v-icon v-html="mdiShuffleVariant" />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="hint in LOCATION_HINTS" :key="'location-' + hint"
                  @click="
                    design.event = { ...design.event, ...generateLocation(hint) };
                    applyEventLinks();
                    $refs['event-location-form'].showAddress = true;
                  "
                >
                  <v-list-item-title v-text="hint" />
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <v-textarea
            class="mb-4 inline-error"
            outlined auto-grow rows="1" persistent-placeholder label="Event name"
            v-model="design.event.name"
          />
          <div class="d-flex flex-wrap ma-n2">
            <input-date
              class="ma-2"
              label="Date"
              v-model="design.event.date_start"
            />
            <div class="flex-grow-1 d-flex">
              <input-time
                class="ma-2 flex-grow-1"
                label="Start time"
                v-model="design.event.time_start"
              />
              <input-time
                class="ma-2 flex-grow-1"
                label="End time"
                :hint="(design.event.time_end && (design.event.time_end <= design.event.time_start)) ? '+1 day' : null"
                v-model="design.event.time_end"
              />
            </div>
            <div class="mt-n2 mx-2 mb-1">
              <input-timezone v-model="design.event.tz" />
            </div>
          </div>

          <event-location-form ref="event-location-form" class="mt-n1" :event="design.event" />

        </v-card-text>
        <v-divider />
        <v-card-title>Preset layouts</v-card-title>
        <v-card-subtitle>Jump-start your invititation's text layout by adding one of these presets. You can still make edits later.<br><span class="warning--text"><v-icon small color="warning" v-html="mdiAlert" /> This will replace all existing smart text fields on the selected page.</span></v-card-subtitle>
        <v-card-text>
          <v-row>
            <v-col v-for="layout, i in eventLayoutPresets" :key="i" cols="4">
              <v-card hover tile @click="selectLayout(layout)">
                <card-preview :card="layout" />
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </dialog-scroll-lock>

    <!-- TODO: switch all these confirms to use the 'confirm' mixin as in the purchase dialog -->

    <dialog-scroll-lock v-model="showDeleteConfirm" width="500">
      <v-card>
        <v-card-title>
          <div>Delete design</div>
          <v-spacer />
          <v-btn icon @click="showDeleteConfirm = false"><v-icon>{{mdiClose}}</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          Are you sure you want to delete this design? This action cannot be undone.
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn @click="deleteDesign" :loading="deleteDesign.pending" color="warning">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock v-model="showSubmitConfirm" width="500">
      <v-card>
        <v-card-title>
          <div>Submit design</div>
          <v-spacer />
          <v-btn icon @click="showSubmitConfirm = false"><v-icon v-html="mdiClose" /></v-btn>
        </v-card-title>
        <v-divider />
        <template v-if="animatedBlobs.length">
          <v-card-text class="pt-4">
            <div class="overline">Printing</div>
            <div class="mb-4">Your card contains animated resources. Please upload a static version of those resources (with the same aspect ratio) so the card can be printed.</div>
            <v-row class="mb-n9">
              <v-col cols="6" class="overline text-center">Animated</v-col>
              <v-col cols="6" class="overline text-center">Static</v-col>
            </v-row>
            <v-row>
              <template v-for="blob in animatedBlobs">
                <v-col cols="6">
                  <v-responsive :aspect-ratio="blob.w / blob.h">
                    <image-blob :blob="blob" />
                  </v-responsive>
                </v-col>
                <v-col class="relative d-flex justify-center" cols="6">
                  <v-responsive v-bind:class="{ 'no-static-img grey lighten-4': !blob.img.src_static }" :aspect-ratio="blob.w / blob.h">
                    <image-blob v-if="blob.img.src_static" :blob="blob" :static="true" />
                  </v-responsive>
                  <v-btn
                    style="bottom: 0;"
                    absolute
                    :color="blob.img.src_static ? null : 'primary'"
                    :loading="handleUploadStaticStatus.pending && (targetBlob.id == blob.id)"
                    @click="promptUploadStatic(blob)"
                  >
                    {{blob.img.src_static ? 'Replace…' : 'Upload…'}}
                  </v-btn>
                </v-col>
              </template>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="6">
                <div class="overline text-center">Web preview</div>
                <card-preview :card="design" mode="preview" />
              </v-col>
              <v-col cols="6">
                <div class="overline text-center">Print preview</div>
                <card-preview :card="design" mode="preview" :static="true" />
              </v-col>
            </v-row>
          </v-card-text>
        </template>

        <v-card-text v-else class="pt-4">
          <v-card class="mx-auto" style="max-width: 300px;">
            <card-preview mode="preview" :card="design" />
          </v-card>
        </v-card-text>
        <v-divider />
        <v-card-text class="pt-4">
          Are you sure you're ready to submit this design? You won't be able to edit it again{{(design || {}).private ? '' : ' unless we ask for revisions'}}.
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showSubmitConfirm = false">Cancel</v-btn>
          <v-btn @click="submitDesign" :disabled="isSubmitDisabled" :loading="loadAnimatedImgBlobSrcsStatus.pending || submitDesign.pending" color="primary">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <v-menu
      v-if="showMediaContextMenu"
      absolute
      :close-on-content-click="false"
      :position-x="showMediaContextMenu.x"
      :position-y="showMediaContextMenu.y"
      :value="true"
      @input="showMediaContextMenu = null"
    >
      <v-card width="300">
        <v-btn class="float-right ma-3 mb-0" icon @click="showMediaContextMenu = null">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <div class="pt-5 px-4">
          <div class="text--label-active">Type</div>
          <div class="overline short">{{showMediaContextMenu.media.type || '–'}}</div>
        </div>
        <v-card-text>
          <v-textarea
            v-if="global.profile?.admin"
            dense label="Description" outlined hide-details auto-grow rows="4"
            v-model="showMediaContextMenu.stagedDescription"
          />
          <template v-else>
            <div class="text--label-active">Description</div>
            <div>{{showMediaContextMenu.stagedDescription}}</div>
          </template>
        </v-card-text>
        <v-card-actions v-if="global.profile?.admin">
          <v-btn
            text color="error"
            :loading="deleteMediaStatus.pending"
            @click="deleteMedia(showMediaContextMenu.media.id)"
          >
            Delete
          </v-btn>
          <v-spacer />
          <v-btn
            text color="primary"
            :disabled="showMediaContextMenu.stagedDescription == showMediaContextMenu.media.description"
            :loading="updateMediaDescriptionStatus.pending"
            @click="updateMediaDescription(showMediaContextMenu.media.id, showMediaContextMenu.stagedDescription)"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <div class="fill-height d-flex flex-column">
      <div class="flex-grow-0">
        <div class="text-right float-sm-right ma-2 mb-0">
          <v-btn icon @click="showHelp = true">
            <v-icon v-html="mdiHelpCircle" />
          </v-btn>

          <v-btn
            icon
            :disabled="[ 'UP', 'SUBMITTED' ].includes(design.status)"
            :input-value="editEvent"
            @click="editEvent = !editEvent"
          >
            <v-icon v-html="mdiCalendar" />
          </v-btn>

          <v-btn icon :color="editTheme ? 'orange' : null" :input-value="editTheme" @click="editTheme = !editTheme">
            <v-icon v-html="mdiPalette" />
          </v-btn>

          <template v-if="!isApplying">
            <v-btn icon target="_blank" :to="{ name: 'catalog', params: { selectedId: designId } }">
              <v-icon v-html="mdiOpenInNew" />
            </v-btn>

            <v-btn icon :loading="cloneStatus.pending" @click="clone">
              <v-icon v-html="mdiContentCopy" />
            </v-btn>

            <v-btn icon :disabled="isReadonly" @click="showDeleteConfirm = true">
              <v-icon v-html="mdiDelete" />
            </v-btn>

            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  icon
                  :input-value="mode != 'design' && !isReadonly"
                  :color="mode != 'design' && !isReadonly ? 'orange' : null"
                >
                  <v-icon v-html="mdiFileFind" />
                </v-btn>
              </template>
              <v-list class="pa-0">
                <v-list-item v-for="[ i, j ] in modes" :key="i" :disabled="isReadonly && i == 'design'" @click="mode = i">
                  <v-list-item-icon>
                    <v-icon v-if="i == mode" class="my-n2" v-html="mdiCheck" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title v-html="j" />
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>

            <design-org-admin-btn
              v-if="global.profile?.org_admin"
              class="d-inline-block"
              scope="card"
              :target-id="designId"
            />
          </template>

          <tag-editor :target-id="designId" :readonly="isReadonly" scope="card" />

          <v-btn
            v-if="isApplying"
            class="ml-2"
            small color="primary"
            :loading="saveDesignStatus.pending"
            @click="saveDesign"
          >
            Done
          </v-btn>

          <template v-else>
            <v-btn
              icon
              :disabled="!design || [ 'UP', 'SUBMITTED', 'DOWN' ].includes(design.status)"
              @click="trySubmit"
            >
              <v-icon v-html="mdiFileUpload" />
            </v-btn>

            <!-- TODO: replace this button with a context menu that allows you to cross-publish across different categories by changing the text -->
            <!--
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn icon :loading="cloneStatus.pending">
                  <v-icon v-html="mdiContentCopy" />
                </v-btn>
              </template>
              <v-list class="pa-0">
                <v-list-item @click="clone">
                  <v-list-item-icon>
                    <v-icon class="my-n2" v-html="mdiContentCopy" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Clone</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="crossPublish">
                  <v-list-item-icon>
                    <v-icon class="my-n2" v-html="mdiShuffle" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Clone</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            -->

            <v-btn
              v-if="isReadonly && (!dirty || !global.profile.admin)"
              icon color="warning"
              @click="$confirm({
                title: 'Not editable',
                msg: `${(design.status == 'SUBMITTED') ? 'This design is pending review and cannot be edited.' : 'This design is currently listed in the public Ellacard catalog and cannot be edited.'} You can still create a copy of this design and edit the copy.${global.profile.admin ? '<br><br><i>As an admin, you can still make changes to the theme without cloning / re-submitting the design.</i>' : ''}`,
                buttons: [ 'Ok' ],
                primary: 'Ok'
              })"
            >
              <v-icon v-html="mdiAlert" />
            </v-btn>
            <v-btn
              v-else
              icon
              :input-value="dirty"
              :loading="saveDesignStatus.pending"
              :disabled="!design || !dirty"
              :color="dirty ? 'primary' : null"
              @click="saveDesign"
            >
              <v-icon v-html="mdiCheck" />
            </v-btn>

            <v-btn icon exact :to="{ name: 'account-design-cards', params: {} }">
              <v-icon v-html="mdiClose" />
            </v-btn>
          </template>
        </div>

        <v-text-field
          v-if="design"
          :key="designId"
          class="ma-2"
          style="max-width: 400px;"
          :disabled="isReadonly"
          v-model="design.name"
          hide-details outlined dense single-line label="Design name"
          :error="!design.name"
        />
        <v-progress-circular v-else indeterminate />
      </div>

      <v-divider />

      <div class="flex-grow-1 px-0 d-flex">
        <div class="flex-grow-1 overflow-hidden relative">
          <template v-if="design">
            <div v-if="editTheme" class="fill-height d-flex flex-column pointer-events-auto">
              <order-stepper-theme
                :org-skin="isReadonly && !global.profile.admin ? design.skin : {}"
                :order="{ ...design, info: { to: '[&nbsp;recipient&nbsp;name&nbsp;]', from: '[&nbsp;sender&nbsp;name&nbsp;]' } }"
                :no-footer="true"
                :disable-to="true"
                :disable-from="true"
                :disable-multiple-recipients="true"
                :disable-logo="true"
                @input="({ skin }) => $set(design, 'skin', skin)"
              />
            </div>
            <template v-else>
              <card-background :src="design.skin.page_background_img" />
              <card-editor
                ref="card-editor"
                is-owner
                :key="mode" :card="design" :mode="mode"
                @select-blob="x => selected = x"
                @settings="show = 'settings'"
                @event-layout="show = 'event-layout'"
                @set-static-img="promptUploadStatic"
                @toggle-media-library="showMediaLibrary = !showMediaLibrary"
              />
            </template>
          </template>
        </div>

        <div
          v-if="showMediaLibrary"
          class="elevation-20 white fill-height d-flex flex-column"
          style="z-index: 10; right: 0; width: 600px; max-width: 90%; height: 100%;"
          v-bind:style="{ position: $vuetify.breakpoint.smAndDown ? 'fixed' : null }"
          @click.stop
        >

          <div class="flex-grow-0 d-flex flex-wrap align-end mt-3 mx-3">
            <div class="text--label-active">Media library</div>
            <v-spacer />
            <div class="ma-n1">
              <v-btn v-if="global.profile?.admin" icon @click="showAddMedia = true"><v-icon v-html="mdiPlus" /></v-btn>
              <v-btn v-if="$vuetify.breakpoint.smAndDown" icon @click="showMediaLibrary = false">
                <v-icon v-html="mdiClose" />
              </v-btn>
            </div>
          </div>

          <v-text-field
            class="flex-grow-0 ma-3"
            outlined dense rounded hide-details clearable single-line label="Filter"
            v-model="mediaFilter"
            @keydown.enter="() => null"
            @click.stop.prevent
            @click:clear="() => null"
          />

          <v-progress-linear v-if="loadMediaLibraryStatus.pending" indeterminate />
          <v-virtual-scroll-grid
            v-else
            class="flex-grow-1"
            bench="1"
            :items="filteredMedia"
            :n="$vuetify.breakpoint.xsOnly ? 4 : 6"
            :aspect-ratio="1"
            :padding="12"
          >
            <template v-slot:default="{ item: media, index }">
              <div
                class="media-item overflow-visible"
                @click="() => {
                  if (!showMediaContextMenu)
                    insertBlob({
                      type: 'IMG',
                      x: 300,
                      y: 420,
                      w: media.w,
                      h: media.h,
                      img: (
                        media.is_animated ? {
                          src: `//cdn.ellacard.com/design-media-items/${media.key}.mp4`,
                          src_static: `//cdn.ellacard.com/design-media-items/${media.key}.webp`,
                          is_mp4: true
                        } : {
                          src: `//cdn.ellacard.com/design-media-items/${media.key}.webp`
                        }
                      )
                    })
                }"
                @contextmenu="e => {
                  e.preventDefault();
                  showMediaContextMenu = { media, x: e.clientX, y: e.clientY, stagedDescription: media.description };
                }"
              >
                <video
                  v-if="media.is_animated"
                  playsinline autoplay muted loop
                  :width="100 * Math.min(1, media.w / media.h) + '%'"
                  :poster="`//cdn.ellacard.com/design-media-items/${media.key}.webp`"
                  :src="`//cdn.ellacard.com/design-media-items/${media.key}-preview.mp4`"
                />
                <img
                  v-else
                  :width="100 * Math.min(1, media.w / media.h) + '%'"
                  :src="`//cdn.ellacard.com/design-media-items/${media.key}.webp`"
                />
              </div>
            </template>
          </v-virtual-scroll-grid>

        </div>

      </div>
    </div>
  </div>
</template>


<style scoped>
.media-item video, .media-item img {
  transition: filter .12s, outline .12s;
  outline: thin dashed #DDD0;
  outline-offset: 8px;
  overflow: hidden;
  display: block;
  margin: auto;
}
.media-item video:hover, .media-item img:hover {
  outline: thin dashed #DDD;
  -webkit-filter: drop-shadow(0 0 4px rgb(0 0 0 / 20%));
}

.no-static-img {
  outline: thin dashed #F44336;
}
</style>


<script>
import CardBackground from '@/components/CardBackground.vue';
import CardEditor from '@/components/CardEditor.vue';
import CardPreview from '@/components/CardPreview.vue';
import DesignOrgAdminBtn from '@/components/DesignOrgAdminBtn.vue';
import EventLocationForm from '@/components/EventLocationForm.vue';
import FormBuilder from '@/components/FormBuilder.vue';
import ImageBlob from '@/components/ImageBlob.vue';
import InputDate from '@/components/InputDate.vue';
import InputTime from '@/components/InputTime.vue';
import InputTimezone from '@/components/InputTimezone.vue';
import OrderStepperEvent from '@/components/OrderStepperEvent.vue';
import OrderStepperTheme from '@/components/OrderStepperTheme.vue';
import TagEditor from '@/components/TagEditor.vue';
import VVirtualScrollGrid from '@/components/VVirtualScrollGrid.vue';
import { VSelect, VTextField } from 'vuetify/lib';
import globalStorage from '@/mixins/global-storage.js';
import getBlobMediaFiles from '@/utils/card-media.js';
import { updateEventLinks } from '@/utils/event-links.js';
import { getImgDimensions, processImageFile, isAnimated } from '@/utils/image.js';
import { blankDesignId, publicDesign } from '@/mixins/public-design.js';
import modernize from '@/utils/order-modernize.js';
import { NAME_HINTS, DATETIME_HINTS, LOCATION_HINTS, generateName, generateDatetime, generateLocation } from '@/utils/random-event.js';
import { DEFAULT as DEFAULT_SKIN } from '@/utils/skin.js';
import { getDateStr, getISO, getTimeRangeStr, getTimezoneName } from '@/utils/time.js';
import uuid4 from '@/utils/uuid.js';
import { textSizeRange } from '@/card-customization-options.js';
import iconPathBlobSettings from 'raw-loader!@/assets/icon-path-blob-settings.txt';
import iconPathPageSettings from 'raw-loader!@/assets/icon-path-page-settings.txt';
import { mdiClose, mdiCheck, mdiChevronUp, mdiFileUpload, mdiTexture, mdiHelpCircle, mdiDelete, mdiTag, mdiFitToPageOutline, mdiImage, mdiTextBox, mdiTextBoxMultiple, mdiAccountBoxMultiple, mdiFileFind, mdiContentCopy, mdiCog, mdiAlert, mdiOpenInNew, mdiCalendarPlus, mdiPageLayoutBody, mdiPalette, mdiCalendar, mdiMenuDown, mdiShuffle, mdiPlus, mdiImageEdit, mdiShuffleVariant } from '@mdi/js';


const CSS_OPTS = {
  'line-height': { is: 'v-text-field' },
  'letter-spacing': { is: 'v-text-field' },
  'text-shadow': { is: 'v-text-field' },
  'text-decoration': { is: 'v-text-field' },
  'text-transform': { is: 'v-select', items: [ '', 'capitalize', 'uppercase', 'lowercase' ] },
  '-webkit-text-stroke': { is: 'v-text-field' }
};

const LAYOUT_DESIGN_IDS = [
  blankDesignId,
  'e370ac27-e140-46e8-8934-0bc3acc6def4',
  '61ba1530-5092-4bb3-b112-851c8131e608'
];


export default {
  name: 'design-edit',

  components: {
    CardBackground,
    CardEditor,
    CardPreview,
    DesignOrgAdminBtn,
    EventLocationForm,
    FormBuilder,
    ImageBlob,
    InputDate,
    InputTime,
    InputTimezone,
    OrderStepperEvent,
    OrderStepperTheme,
    TagEditor,
    VSelect,
    VTextField,
    VVirtualScrollGrid
  },

  mixins: [ publicDesign ],

  props: [ 'designId', 'isApplying' ],

  data() {
    return {
      showHelp: false,
      mode: 'design',
      editTheme: false,
      editEvent: false,
      eventLayoutPresets: [],

      design: null,
      loadDesignStatus: this.$track('loadDesign'),

      dirty: false,
      saveDesignStatus: this.$track('saveDesign'),

      showSubmitConfirm: false,
      submitDesignStatus: this.$track('submitDesign'),

      showDeleteConfirm: false,
      deleteDesignStatus: this.$track('deleteDesign'),

      show: 'settings',
      showEventLink: false,
      selected: null,

      handleUploadStatus: this.$track('handleUpload'),

      targetBlob: null,
      handleUploadStaticStatus: this.$track('handleUploadStatic'),

      cloneStatus: this.$track('clone'),

      showMediaLibrary: false,
      loadMediaLibraryStatus: this.$track('loadMediaLibrary'),
      showAddMedia: false,
      addMedia: {
        type: null,
        img: null,
        video: null,
        description: ''
      },
      submitMediaStatus: this.$track('submitMedia'),
      media: null,
      mediaFilter: '',
      showMediaContextMenu: null,
      deleteMediaStatus: this.$track('deleteMedia'),
      updateMediaDescriptionStatus: this.$track('updateMediaDescription'),

      animatedImgBlobSrcs: {},
      loadAnimatedImgBlobSrcsStatus: this.$track('loadAnimatedImgBlobSrcs')
    };
  },

  computed: {
    isReadonly() {
      return !this.design || ([ 'UP', 'SUBMITTED' ].includes(this.design.status) && !this.design.private)
    },

    animatedBlobs() {
      const blobs = [];
      for (const page of (this.design && this.design.pages || []))
        for (const blob of page.blobs)
          if ((blob.type == 'IMG') && (blob.img.try_webp || blob.img.is_mp4 || blob.img.src_static))
            blobs.push(blob);
      return blobs;

      isAnimated
    },

    // staticDesign() {
    //   if (!this.design)
    //     return null;
    //   const design = _.cloneDeep(this.design);
    //   for (const page of design.pages) {
    //     for (const blob of page.blobs) {
    //       if ((blob.type == 'IMG') && blob.img.src_static) {
    //         blob.img.src = blob.img.src_static;
    //         blob.img.is_mp4 = false;
    //         blob.img.try_webp = false;
    //       }
    //     }
    //   }
    //   return design;
    // },

    isSubmitDisabled() {
      return this.loadAnimatedImgBlobSrcsStatus.pending || (this.animatedBlobs.filter(x => !x.img.src_static).length > 0);
    },

    selectedBounds() {
      return this.selected && [ this.selected.id, this.selected.w, this.selected.h ];
    },

    designEventKey() {
      return JSON.stringify(this.design.event);
    },

    filteredMedia() {
      if (!this.media)
        return [];
      const filter = (this.mediaFilter || '').toLowerCase();
      return this.media.filter(x => x.type?.includes(filter) || x.description.includes(filter));
    }
  },

  created() {
    Object.assign(this, {
      CSS_OPTS,
      EMPTY_FORM: { inputs: [] },
      EMPTY_HIGHLIGHT_FIELDS: new Set(),
      NAME_HINTS,
      DATETIME_HINTS,
      LOCATION_HINTS,
      generateName,
      generateDatetime,
      generateLocation,
      modes: [
        [ 'design', 'Design' ],
        [ 'preview', 'Preview in catalog' ],
        [ 'edit', 'Preview as editor' ],
        [ 'view', 'Preview as recipient' ]
      ],
      textSizeRange,
      iconPathBlobSettings,
      iconPathPageSettings,
      mdiClose,
      mdiCheck,
      mdiChevronUp,
      mdiFileUpload,
      mdiTexture,
      mdiHelpCircle,
      mdiDelete,
      mdiTag,
      mdiFitToPageOutline,
      mdiImage,
      mdiTextBox,
      mdiTextBoxMultiple,
      mdiAccountBoxMultiple,
      mdiFileFind,
      mdiContentCopy,
      mdiCog,
      mdiAlert,
      mdiOpenInNew,
      mdiCalendarPlus,
      mdiPageLayoutBody,
      mdiPalette,
      mdiCalendar,
      mdiMenuDown,
      mdiShuffle,
      mdiPlus,
      mdiImageEdit,
      mdiShuffleVariant
    });
  },

  methods: {
    onNumberInput(prop, value) {
      this.selected[prop] = parseInt(value || 0);
    },

    async setCSS(key, value) {
      const s = this.selected;
      if (!s.text.css)
        this.$set(s.text, 'css', {});
      if (value)
        this.$set(s.text.css, key, value);
      else
        this.$delete(s.text.css, key);
      if (_.isEmpty(s.text.css))
        this.$delete(s.text, 'css');
    },

    async loadDesign() {
      this.design = null;
      const design = await this.$call('get-design', { design_id: this.designId });
      // The 'skin' stored on designs is incomplete (and might not be present at all)
      design.skin = Object.assign({}, DEFAULT_SKIN, design.skin || {});

      // Legacy designs did not have a default event set
      if (!design.event)
        design.event = { ...generateName('Birthday'), ...generateDatetime('Afternoon'), ...generateLocation('Home') };

      // Some legacy designs have incomplete event details (note that we only want to modernize the event, not the
      // entire "card" hence the clone)
      const cloned = _.cloneDeep({ ...design, info: {} });
      modernize(cloned);
      design.event = cloned.event;

      this.design = design;
      for (const page of this.design.pages)
        for (const blob of page.blobs)
          blob.user_id = null;
      this.mode = this.isReadonly ? 'preview' : 'design';

      if (!this.design.pages.filter(x => x.blobs.length).length)
        this.showHelp = true;

      // Shouldn't be "dirty" right after we load a design
      await this.$nextTick();
      this.dirty = false;
    },

    async saveDesign() {
      // In case we were previewing in edit mode, switching back to design mode will reset everything, but we want to
      // make sure that any changes we make to the skin persist regardless of what mode we're in
      const skin = _.cloneDeep(this.design.skin || {});
      this.mode = 'design';
      await this.$nextTick();
      this.design.skin = skin;

      const files = await getBlobMediaFiles(this.design);
      this.design = await this.$call('update-design', { design_id: this.designId, design: this.design, files });

      // Make sure that our template mask placeholders picked up any updates to the 'src'
      for (const page of this.design.pages)
        for (const blob of page.blobs)
          if (blob.template && blob.type == 'IMG')
            blob.mask.placeholder = blob.img.src;

      this.$emit('update', this.design);

      await this.$nextTick();
      this.dirty = false;
    },

    async submitDesign() {
      this.mode = 'design';
      this.design = await this.$call('submit-design', { design_id: this.designId });
      this.$emit('update', this.design);
      this.$router.push({ name: 'account-design-cards', params: {} });
    },

    async deleteDesign() {
      await this.$call('delete-design', { design_id: this.designId });
      this.$emit('delete');
      this.$router.push({ name: 'account-design-cards', params: {} });
    },

    clearPlaceholder() {
      this.selected.img.src = '/template-placeholder.png';
      this.selected.mask.placeholder = '/template-placeholder.png';
    },

    async handleUpload(rawFile) {
      const { url, w, h } = await processImageFile(rawFile, this.selected.w, this.selected.h, true);
      const file = await fetch(url).then(r => r.blob());
      const src = await this.$call('upload-design-img-template-placeholder', { file });
      this.selected.img.src = src;
      this.selected.mask.placeholder = src;
    },

    promptUploadPlaceholder() {
      const el = this.$refs['upload-image-input'];
      const onChange = e => {
        if (e.target.files[0])
          this.handleUpload(e.target.files[0]);
        e.target.onchange = null;
        e.target.value = null;
      };
      el.onchange = onChange;
      el.click();
    },

    async handleUploadStatic(rawFile, blob) {
      this.targetBlob = blob;
      try {
        if (rawFile.size > 10485760)
          throw new VisibleError('Image should not be larger than 10 MB');
        const url = URL.createObjectURL(rawFile);
        const { img, w, h } = await getImgDimensions(url);
        if (w < blob.w || h < blob.h)
          throw new VisibleError(`Image should be at least ${blob.w}x${blob.h} (preferably twice that)`);
        const file = await fetch(url).then(r => r.blob());
        const params = { design_id: this.designId, blob_id: blob.id, file };
        const src = await this.$call('set-design-blob-static-resource', params);
        this.$set(blob.img, 'src_static', src);
      } catch (e) {
        throw e;
      } finally {
        this.targetBlob = null;
      }
    },

    promptUploadStatic(blob) {
      const el = this.$refs['upload-static-image-input'];
      const onChange = e => {
        if (e.target.files[0])
          this.handleUploadStatic(e.target.files[0], blob);
        e.target.onchange = null;
        e.target.value = null;
      };
      el.onchange = onChange;
      el.click();
    },

    async clone() {
      const r = await this.$confirm({
        title: 'Clone design',
        msg: 'Do you want to include all the tags that have already been added to this design?',
        buttons: [ 'Cancel', 'No', 'Yes' ],
        primary: 'Yes'
      });
      if (r == 'Cancel')
        return;
      const designId = await this.$call('clone-design', { design_id: this.designId, include_tags: r == 'Yes' });
      this.$router.push({ name: 'account-design-cards', params: { designId } });
    },

    async trySubmit() {
      if (!this.global.profile.designer)
        return this.$confirm({
          title: 'Please register first',
          msg: 'You must be a registered designer to submit designs to the public Ellacard catalog. You can <a target="_blank" href="https://ellacard.com/account/design/overview">register here</a>.',
          buttons: [ 'Ok' ],
          primary: 'Ok'
        });
      if (this.dirty)
        await this.saveDesign();
      this.showSubmitConfirm = true
    },

    async loadMediaLibrary() {
      const output = await this.$call('list-design-media-library-items');
      this.media = output.map(([ id, media ]) => ({ id, ...media, description: media.description.toLowerCase() }));
      this.media.sort((a, b) => a.created_at.localeCompare(b.created_at));
    },

    async deleteMedia(id) {
      const r = await this.$confirm({ title: 'Are you sure?', msg: 'Are you sure you want to delete this item?' });
      if (r == 'No')
        return;
      await this.$call('delete-design-media-library-item', { id });
      this.media = this.media.filter(x => x.id != id);
      this.showMediaContextMenu = null;
    },

    async updateMediaDescription(id, description) {
      await this.$call('update-design-media-library-item-description', { id, description });
      for (const x of this.media)
        if (x.id == id)
          x.description = description;
      this.showMediaContextMenu = null;
    },

    async loadLayouts() {
      this.eventLayoutPresets = await Promise.all(LAYOUT_DESIGN_IDS.map(this.getPublicDesign));
    },

    selectLayout(layout) {
      const page = this.$refs['card-editor'].selectedPage;
      page.blobs = page.blobs.filter(x => !x.text?.event_link?.format);
      page.blobs.push(...layout.pages[0].blobs.map(x => ({ id: uuid4(), ..._.cloneDeep(x) })));
      this.editEvent = false;
    },

    applyEventLinks() {
      updateEventLinks(this.design, this.design.event);
    },

    async insertBlob(partial) {
      const blob = Object.assign({ id: uuid4(), user_id: null, x: 300, y: 420, w: 600, h: 840, r: 0 }, partial);
      this.$refs['card-editor'].selectedPage.blobs.push(blob);
      await new Promise(setTimeout)
      this.$refs['card-editor'].selected = blob;
    },

    async submitMedia() {
      const [ id, media ] = await this.$call('add-design-media-library-item', this.addMedia);
      this.media = [ { id, ...media }, ...this.media ];
      this.$notify('Media uploaded successfully');
      this.showAddMedia = false;
      this.addMedia.img = null;
      this.addMedia.video = null;
      this.addMedia.description = '';
    },

    async loadAnimatedImgBlobSrcs() {
      for (const page of this.design.pages)
        for (const blob of page.blobs)
          if ((blob.type == 'IMG') && !this.animatedImgBlobSrcs.hasOwnProperty(blob.img.src))
            this.$set(
              this.animatedImgBlobSrcs,
              blob.img.src,
              blob.img.src_static ? true : (await isAnimated(blob.img.src))
            );
    }
  },

  beforeRouteEnter(to, from, next) {
    // Note: we have no access to 'this' because the component has not yet been instantiated
    const global = globalStorage.data().global;
    if (global.profile && global.profile.designer)
      next();
    else
      next({ name: 'design', params: { tab: 'cards' } });
  },

  watch: {
    showMediaLibrary: {
      immediate: true,
      handler(v) {
        if (v && !this.media) {
          this.media = [];
          this.loadMediaLibrary();
        }
      }
    },

    designId: {
      immediate: true,
      handler() {
        this.selected = null;
        this.loadDesign();
      }
    },

    design: {
      deep: true,
      handler() {
        this.dirty = true;
        this.loadAnimatedImgBlobSrcs();
      }
    },

    mode(cur, prev) {
      this.selected = null;
      if (prev == 'design')
        this.stashed = _.cloneDeep(this.design);
      if (cur == 'design') {
        this.design = this.stashed;
      } else if (cur == 'edit') {
        const cloned = _.cloneDeep(this.design);
        cloned.event ||= {};
        modernize(cloned);
        this.design = cloned;
      }
    },

    selected: {
      deep: true,
      async handler(v) {
        if (!v)
          return;
        if ((v.type == 'TEXT') && v.template) {
          // Inputs from the detail control panel will not have triggered text auto-scaling for templates
          await this.$nextTick();
          this.$refs['card-editor'].$refs[v.id][0].$refs.text.autoScaleText();
          return;
        }
        if ((v.type == 'IMG') && v.img.gradient) {
          this.gradientStart = v.img.gradient.start;
          this.gradientStop = v.img.gradient.stop;
          this.gradientDirection = v.img.gradient.direction;
          return;
        }
      }
    },

    selectedBounds(cur, prev) {
      const s = this.selected;
      if (cur && prev && (cur[0] == prev[0]) && (s.type == 'IMG') && s.template && s.mask.placeholder)
        // We want to avoid distortion of placeholder images for image templates, so if we change the dimensions after
        // we've already set one, let's clear it out
        this.clearPlaceholder();
    },

    editEvent(v) {
      if (v) {
        if (!this.eventLayoutPresets.length)
          this.loadLayouts();
      } else {
        // TODO: this logic is duplicated in the order-stepper-event's 'datetime' watcher
        const e = this.design.event;
        e.from = getISO(e.date_start, e.time_start, e.tz);
        e.to = getISO(e.date_start, e.time_end, e.tz);
        if (e.from && e.to && (new Date(e.to) <= new Date(e.from))) {
          const toDate = new Date(e.to);
          toDate.setDate(toDate.getDate() + 1);
          e.to = toDate.toISOString();
        }
        e.lz_date = null;
        e.lz_time_range = null;
        e.lz_tz_name = null;
        if (e.from) {
          const fromDate = new Date(e.from);
          const toDate = e.to ? new Date(e.to) : null;
          e.lz_date = getDateStr(fromDate, e.tz);
          e.lz_time_range = getTimeRangeStr(fromDate, toDate, e.tz);
          e.lz_tz_name = getTimezoneName(e.from, 'short', e.tz);
        }
        this.applyEventLinks();
      }
    }
  }
}
</script>
